import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "../styles/adminKyc.css";

const API_BASE_URL = "https://primerise.space";

export default function AdminKyc() {
  const [kycList, setKycList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedKyc, setSelectedKyc] = useState(null);

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("primeriseToken") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const clearAuthStorage = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("primeriseToken");
    localStorage.removeItem("primeriseAuth");
    localStorage.removeItem("primeriseCurrentUser");
    localStorage.removeItem("naxoraToken");
    localStorage.removeItem("naxoraAuth");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("naxoraCurrentUser");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("user");
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Please login again",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    clearAuthStorage();
    window.location.replace("/login");
  };

  const normalizeStatus = (status) => String(status || "").toLowerCase();

  const getAuthHeaders = (includeJson = false) => {
    const token = getToken();
    const headers = {};

    if (includeJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const fetchKyc = async () => {
    try {
      setLoading(true);

      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/kyc/all`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || "Failed to load KYC requests");
      }

      setKycList(Array.isArray(data.requests) ? data.requests : []);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to load KYC requests",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKyc();
  }, []);

  const fetchKycDetail = async (id) => {
    try {
      setDetailLoading(true);

      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/kyc/${id}`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || "Failed to load KYC details");
      }

      setSelectedKyc(data.request || null);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to load KYC details",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setSelectedKyc(null);
  };

  const handleApprove = async (id) => {
    try {
      const confirm = await Swal.fire({
        title: "Approve KYC?",
        text: "This user will be marked as verified.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Approve",
        confirmButtonColor: "#d4af37",
        cancelButtonColor: "#6b7280",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      if (!confirm.isConfirmed) return;

      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/kyc/approve/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(true),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || "Failed to approve KYC");
      }

      await Swal.fire({
        icon: "success",
        title: "Approved",
        text: "KYC approved successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      setSelectedKyc(null);
      fetchKyc();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to approve KYC",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const handleReject = async (id) => {
    try {
      const rejectPrompt = await Swal.fire({
        title: "Reject KYC?",
        input: "text",
        inputLabel: "Reason",
        inputPlaceholder: "Enter rejection reason",
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#d4af37",
        cancelButtonColor: "#6b7280",
        background: "#0b0b0b",
        color: "#ffffff",
        inputAttributes: {
          autocapitalize: "off",
          autocorrect: "off",
        },
        preConfirm: (value) => {
          if (!String(value || "").trim()) {
            Swal.showValidationMessage("Rejection reason is required");
          }
          return value;
        },
      });

      if (!rejectPrompt.isConfirmed) return;

      const reason = String(rejectPrompt.value || "").trim();
      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/kyc/reject/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(true),
        body: JSON.stringify({ reason }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || "Failed to reject KYC");
      }

      await Swal.fire({
        icon: "success",
        title: "Rejected",
        text: "KYC rejected successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      setSelectedKyc(null);
      fetchKyc();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to reject KYC",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const renderStatusText = (status) => {
    const value = normalizeStatus(status);

    if (value === "verified") return "Verified";
    if (value === "pending") return "Pending";
    if (value === "rejected") return "Rejected";
    return "Unverified";
  };

  const getImageStatusText = (kyc) => {
    const count = [kyc.hasIdFront, kyc.hasIdBack, kyc.hasSelfie].filter(
      Boolean
    ).length;

    return `${count}/3 images available`;
  };

  if (loading) {
    return <div className="admin-kyc-loading">Loading...</div>;
  }

  return (
    <div className="admin-kyc-page">
      <h1>KYC Requests</h1>

      {kycList.length === 0 ? (
        <div className="admin-kyc-loading">No KYC requests found</div>
      ) : (
        <div className="admin-kyc-grid">
          {kycList.map((kyc) => {
            const status = normalizeStatus(kyc.status);

            return (
              <div key={kyc.id} className="admin-kyc-card">
                <h3>{kyc.fullName || "No Name"}</h3>
                <p>{kyc.email || "-"}</p>
                <p>{kyc.phone || "-"}</p>
                <p>{kyc.country || "-"}</p>
                <p>{kyc.city || "-"}</p>
                <p>{kyc.dateOfBirth || "-"}</p>

                <div className="admin-kyc-status">
                  Status: {renderStatusText(status)}
                </div>

                <div className="admin-kyc-status">
                  Documents: {getImageStatusText(kyc)}
                </div>

                {status === "rejected" && kyc.rejectionReason ? (
                  <div className="admin-kyc-status">
                    Reason: {kyc.rejectionReason}
                  </div>
                ) : null}

                <div className="admin-kyc-actions">
                  <button
                    className="view-btn"
                    onClick={() => fetchKycDetail(kyc.id)}
                    disabled={detailLoading}
                  >
                    {detailLoading ? "Loading..." : "View Details"}
                  </button>

                  {status === "pending" ? (
                    <>
                      <button
                        className="approve-btn"
                        onClick={() => handleApprove(kyc.id)}
                      >
                        Approve
                      </button>

                      <button
                        className="reject-btn"
                        onClick={() => handleReject(kyc.id)}
                      >
                        Reject
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedKyc ? (
        <div className="admin-kyc-modal-overlay">
          <div className="admin-kyc-modal">
            <div className="admin-kyc-modal-header">
              <div>
                <h2>{selectedKyc.fullName || "KYC Details"}</h2>
                <p>{selectedKyc.email || "-"}</p>
              </div>

              <button
                type="button"
                className="admin-kyc-modal-close"
                onClick={closeDetailModal}
              >
                ×
              </button>
            </div>

            <div className="admin-kyc-detail-grid">
              <div>
                <span>Phone</span>
                <strong>{selectedKyc.phone || "-"}</strong>
              </div>

              <div>
                <span>Country</span>
                <strong>{selectedKyc.country || "-"}</strong>
              </div>

              <div>
                <span>City</span>
                <strong>{selectedKyc.city || "-"}</strong>
              </div>

              <div>
                <span>Date of Birth</span>
                <strong>{selectedKyc.dateOfBirth || "-"}</strong>
              </div>

              <div>
                <span>Status</span>
                <strong>{renderStatusText(selectedKyc.status)}</strong>
              </div>

              <div>
                <span>Submitted At</span>
                <strong>{selectedKyc.submittedAt || "-"}</strong>
              </div>
            </div>

            {selectedKyc.rejectionReason ? (
              <div className="admin-kyc-status">
                Reason: {selectedKyc.rejectionReason}
              </div>
            ) : null}

            <div className="admin-kyc-images detail-images">
              <div className="admin-kyc-image-box">
                <span>ID Front</span>
                {selectedKyc.idFrontUrl ? (
                  <img src={selectedKyc.idFrontUrl} alt="ID Front" />
                ) : (
                  <div className="admin-kyc-image-placeholder">
                    No image available
                  </div>
                )}
              </div>

              <div className="admin-kyc-image-box">
                <span>ID Back</span>
                {selectedKyc.idBackUrl ? (
                  <img src={selectedKyc.idBackUrl} alt="ID Back" />
                ) : (
                  <div className="admin-kyc-image-placeholder">
                    No image available
                  </div>
                )}
              </div>

              <div className="admin-kyc-image-box">
                <span>Selfie</span>
                {selectedKyc.selfieUrl ? (
                  <img src={selectedKyc.selfieUrl} alt="Selfie" />
                ) : (
                  <div className="admin-kyc-image-placeholder">
                    No image available
                  </div>
                )}
              </div>
            </div>

            {normalizeStatus(selectedKyc.status) === "pending" ? (
              <div className="admin-kyc-actions modal-actions">
                <button
                  className="approve-btn"
                  onClick={() => handleApprove(selectedKyc.id)}
                >
                  Approve
                </button>

                <button
                  className="reject-btn"
                  onClick={() => handleReject(selectedKyc.id)}
                >
                  Reject
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}